import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  Button,
  Link,
  Popup,
  Dropzoned,
  colors,
  Text,
  AddIcon,
  CheckmarkIcon,
} from '@commonsku/styles';
import DateInput from '../DateInput';
import { LabeledSelect } from '../helpers';
import { useDispatch, useSelector } from 'react-redux';
import { createMessage, updateMessage } from '../../redux/messages';
import { useIdentity } from '../../hooks';
import { createUploadFile } from '../../actions/file';
import MentionTextarea from '../MentionTextarea';
import { getUserDropdown, getUserOptions } from '../../selectors/dropdowns';
import { DisplayClient } from '../../types';

const StyledPopup = styled(Popup)`
  &&& {
    width: 576px;
    height: fit-content;
    max-height: 80vh;
  }
`;

const TaskLabel = styled(Text)`
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  color: ${colors.neutrals[90]};
  margin-bottom: 8px;
  display: block;
`;

const PopupContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const DateInputContainer = styled.div`
  .labeled-input-container {
    width: 100%;
  }
`;

const SelectContainer = styled.div`
  .labeled-select {
    width: 100%;
  }
`;

const AddFileSection = styled.div`
  padding: 0 24px;
  display: flex;
  align-items: center;
  margin-bottom: 16px;
`;

const AddFileLink = styled(Link)`
  color: ${colors.primary1[65]};
  font-size: 14px;
  font-weight: 600;
  line-height: 24px;
  text-decoration: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 8px 24px;
  gap: 4px;
  
  &:hover {
    text-decoration: underline;
  }
`;

const DropzoneContainer = styled.div`
  padding: 0 24px 16px;
  
  > div {
    width: 100%;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 24px;
`;

const ConfirmationPopup = styled(Popup)`
  &&& {
    width: 576px;
    height: fit-content;
    max-height: 80vh;
  }
`;

const ConfirmationContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0;
`;

const SuccessMessage = styled.div`
  &&& {
    background-color: ${colors.green[10]};
    border-radius: 5px;
    padding: 16px;
    display: flex;
    align-items: flex-start;
    gap: 16px;
  }
`;

const SuccessText = styled(Text)`
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  color: #009853;
  flex: 1;
`;

interface TaskCreatedConfirmationProps {
  onClose: () => void;
  client: Pick<DisplayClient, 'id' | 'name'>;
}

const TaskCreatedConfirmation: React.FC<TaskCreatedConfirmationProps> = ({
  onClose,
  client,
}) => {
  return (
    <ConfirmationPopup
      title="Task Created"
      noCloseButton
    >
      <ConfirmationContent>
        <SuccessMessage>
          <CheckmarkIcon size="medium" color={colors.green[80]} />
          <SuccessText>
            The task was created and will appear as a Client Task on the sales dashboard, and on the client page for{' '}
            {client.id ? (
              <Link to={`/client/${client.id}/activity`}>
                {client.name}
              </Link>
            ) : (
              client.name
            )}.
          </SuccessText>
        </SuccessMessage>

        <ActionButtons>
          <Button variant="primary" size="medium" onClick={onClose}>
            Close
          </Button>
        </ActionButtons>
      </ConfirmationContent>
    </ConfirmationPopup>
  );
};

interface CreateTaskPopupProps {
  client: Pick<DisplayClient, 'id' | 'name'>;
  onClose: () => void;
  message?: any;
  onError?: (error: string) => void;
}

interface TaskState {
  message_id: string;
  note_id: string;
  date_reminder: string;
  date_complete: string;
  details_parent_id: string;
  details_parent_type: string;
  details_type: string;
  message_text: string;
  message_type: string;
  note_type: string;
  reminder_user: string;
  tagged_users: string;
}

const CreateTaskPopup: React.FC<CreateTaskPopupProps> = ({
  message = {},
  onClose,
  onError,
  client,
}) => {
  const dispatch = useDispatch();
  const identity = useIdentity();
  const users_options = useSelector(s => getUserOptions(s).map(v => ({
    label: v.value,
    value: v.key,
  })));
  const mention_users = useSelector(getUserDropdown);

  const [selectedFile, setFile] = useState<File | null>(null);
  const [showFiles, setShowFiles] = useState<boolean>(false);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [state, setState] = useState<TaskState>({
    message_id: message.message_id || '',
    note_id: message.note_id || '',
    date_reminder: message.date_reminder || '',
    date_complete: message.date_complete || '',
    details_parent_id: message.details_parent_id || '',
    details_parent_type: message.details_parent_type || '',
    details_type: message.details_type || 'NOTE',
    message_text: message.message_text || '',
    message_type: message.message_type || '',
    note_type: message.note_type || 'NOTE',
    reminder_user: message.reminder_user || _.get(identity, 'user_id', ''),
    tagged_users: message.tagged_users || '',
  });

  const updateField = (field: keyof TaskState, value: string) =>
    setState(s => ({ ...s, [field]: value }));

  const handleSave = async () => {
    if (state.message_id) {
      try {
        const result = await dispatch(updateMessage(state) as any);
        if (result && !(result as any).error) {
          setShowConfirmation(true);
        } else {
          onError?.('Failed to update task.');
        }
      } catch (error) {
        console.error('Error updating task:', error);
        onError?.('Failed to update task.');
      }
      return;
    }

    try {
     const resp: any = await dispatch(createMessage(state) as any);
     if (!resp || resp.error) {
       onError?.('Failed to create task.');
       return;
     }

     if (!selectedFile) {
       setShowConfirmation(true);
       return;
     }

     if (!resp.note_id) {
       onError?.('Failed to upload file.');
       return;
     }

     await uploadFile(resp.note_id, selectedFile);
     setShowConfirmation(true);
    } catch (error) {
      console.error('Error creating task:', error);
      onError?.('Failed to create task.');
    }
  };

  const handleConfirmationClose = () => {
    setShowConfirmation(false);
    onClose && onClose();
  };

  const uploadFile = (parent_id: string, file_blob: File, folder_id: string | null = null) => {
    return dispatch(createUploadFile(parent_id, 'NOTE', file_blob, folder_id) as any)
      .catch((error: any) => {
        console.error('UPLOAD ERROR:', error);
        throw error;
      });
  };

  useEffect(() => {
    setState({
      message_id: message.message_id || '',
      note_id: message.note_id || '',
      date_reminder: message.date_reminder || '',
      date_complete: message.date_complete || '',
      details_parent_id: message.details_parent_id || '',
      details_parent_type: message.details_parent_type || '',
      details_type: message.details_type || 'NOTE',
      message_text: message.message_text || '',
      message_type: message.message_type || '',
      note_type: message.note_type || 'NOTE',
      reminder_user: message.reminder_user || _.get(identity, 'user_id', ''),
      tagged_users: message.tagged_users || '',
    });
  }, [identity, message]);

  return (
    <>
      {showConfirmation ? (
        <TaskCreatedConfirmation
          onClose={handleConfirmationClose}
          client={client}
        />
      ) : (
        <StyledPopup
          title="Create Task"
          noCloseButton
        >
          <PopupContent>
            <div>
              <TaskLabel>Task</TaskLabel>
              <MentionTextarea
                mention_users={mention_users}
                value={state.message_text}
                onChange={(value: string) => updateField('message_text', value)}
                placeholder="What needs to be done?"
              />
            </div>

            <DateInputContainer>
              <TaskLabel>Task Date</TaskLabel>
              <DateInput
                showMonthDropdown
                showYearDropdown
                selected={state.date_reminder}
                onChange={(dateStr: string) => updateField('date_reminder', dateStr)}
                portalId="root-portal"
                style={{ width: '100%' }}
              />
            </DateInputContainer>

            <SelectContainer>
              <TaskLabel>Assign To</TaskLabel>
              <LabeledSelect
                label=""
                name="task_assigned_to"
                options={users_options}
                value={state.reminder_user}
                onChange={(v: any) => updateField('reminder_user', v.value)}
                inPopup
                placeholder="Select a Rep"
              />
            </SelectContainer>

            <AddFileSection>
              <AddFileLink onClick={() => setShowFiles(s => !s)}>
                <AddIcon width="14px" />
                Add File
              </AddFileLink>
            </AddFileSection>

            {showFiles && (
              <DropzoneContainer>
                <Dropzoned
                  placeholder="Upload Files"
                  showDroppedFiles={true}
                  onDrop={(acceptedFiles: File[]) => {
                    if (acceptedFiles.length > 0) {
                      setFile(acceptedFiles[0]);
                    }
                  }}
                />
              </DropzoneContainer>
            )}

            <ActionButtons>
              <Button variant="secondary" size="medium" onClick={onClose}>
                Cancel
              </Button>
              <Button variant="primary" size="medium" onClick={handleSave}>
                Create
              </Button>
            </ActionButtons>
          </PopupContent>
        </StyledPopup>
      )}
    </>
  );
};

export default CreateTaskPopup;
